import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Chip,
    Typography,
} from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'

import { AppRoutes, resolveRoute } from '../../helpers/Routes'
import { TenantInspectionHubMoveIn } from '../../types/inspection'
import { LifecycleSection, InspectionEmptyState } from './InspectionSectionCard'
import { InspectionSummaryCard } from './InspectionSummaryCard'
import { MoveInSurveyDialog } from './MoveInSurveyDialog'
import {
    countAreas,
    flattenTenantIssuesFromDetails,
    getEarliestDueDate,
    getLatestCompletedDate,
    tenantIssuesToPreviewItems,
} from './inspectionUtils'
import { isMoveInSurveySubmitted } from '../../helpers/LocalStorage'

interface MoveInSectionProps {
    moveIn: TenantInspectionHubMoveIn
    leaseId: number
    defaultExpanded?: boolean
    onRefresh?: () => void
}

export const MoveInSection: React.FC<MoveInSectionProps> = ({
    moveIn,
    leaseId,
    defaultExpanded = false,
    onRefresh,
}) => {
    const navigate = useNavigate()
    const [surveyDismissed, setSurveyDismissed] = useState(() => {
        return isMoveInSurveySubmitted(leaseId)
    })

    const showSurvey =
        moveIn.state === 'complete' &&
        moveIn.survey_pending === true &&
        !surveyDismissed

    const handleSurveySuccess = () => {
        setSurveyDismissed(true)
        onRefresh?.()
    }

    const handleCompleteMoveIn = () => {
        navigate(resolveRoute(AppRoutes.selectArea, ':id', leaseId))
    }

    const handleViewResults = () => {
        navigate(AppRoutes.inspectionMoveIn)
    }

    const getSectionStatus = () => {
        if (moveIn.state === 'complete') return 'complete' as const
        if (moveIn.state === 'action_required') return 'action_required' as const
        return 'not_available' as const
    }

    const getSummary = () => {
        if (moveIn.state === 'not_available') return 'Not available yet'
        if (moveIn.state === 'action_required') {
            return `${moveIn.areas.length} area${moveIn.areas.length === 1 ? '' : 's'} need inspection`
        }
        const issueCount = flattenTenantIssuesFromDetails(moveIn.detail).length
        if (issueCount > 0) {
            return `Complete · ${issueCount} issue${issueCount === 1 ? '' : 's'} reported`
        }
        return `Complete · ${countAreas(moveIn.detail)} areas inspected`
    }

    const dueDate =
        getEarliestDueDate(moveIn.detail) ??
        moveIn.areas.find((a) => a.due_date)?.due_date ??
        null
    const completedDate = getLatestCompletedDate(moveIn.detail)
    const previewItems = tenantIssuesToPreviewItems(
        flattenTenantIssuesFromDetails(moveIn.detail),
    )
    const areaCount = countAreas(moveIn.detail) || moveIn.areas.length

    return (
        <LifecycleSection
            sectionNumber={1}
            title="Move-In"
            icon={<AssignmentIcon />}
            status={getSectionStatus()}
            summary={getSummary()}
            defaultExpanded={defaultExpanded}
        >
            {moveIn.state === 'not_available' && (
                <InspectionEmptyState message="Not available yet" />
            )}

            {moveIn.state === 'action_required' && (
                <Box>
                    <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}
                    >
                        Complete your move-in inspection for the following areas:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {moveIn.areas.map((area) => (
                            <Box
                                key={`${area.unit_name}-${area.area_label}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#fff' }}>
                                    {area.unit_name} — {area.area_label}
                                </Typography>
                                <Chip
                                    label={area.status}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        color: '#94a3b8',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<AssignmentIcon />}
                        onClick={handleCompleteMoveIn}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Complete move-in now
                    </Button>
                </Box>
            )}

            {moveIn.state === 'complete' && (
                <InspectionSummaryCard
                    icon={<AssignmentIcon sx={{ fontSize: 22 }} />}
                    title="Move-In Inspection"
                    status="complete"
                    dueDate={dueDate}
                    completedDate={completedDate}
                    areaCount={areaCount}
                    previewItems={previewItems}
                    previewVariant="tenant_issues"
                    onViewResults={handleViewResults}
                />
            )}

            <MoveInSurveyDialog
                open={showSurvey}
                leaseId={leaseId}
                onClose={() => setSurveyDismissed(true)}
                onSuccess={handleSurveySuccess}
            />
        </LifecycleSection>
    )
}
